import {
  FACEBOOK,
  GOOGLE,
  GOOGLE_AUTH,
  LOGIN,
  PAGE_LOGIN,
  PAGE_PERMISSION_DENIED,
  PRIVACY_POLICY,
  PROJECT_INVITE,
  RESET,
  SIGNUP,
  VERIFY,
} from '@frontend/sorghum/utils';
import axios, { AxiosRequestHeaders } from 'axios';
import { resolveProjectIDHeader } from './project-id-header';

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') as string;
    // 呼叫端指定專案時 header key 須為小寫 'project-id'（axios 0.27 headers 是一般物件，大小寫敏感）
    const projectID = resolveProjectIDHeader(
      config.headers?.['project-id'],
      sessionStorage.getItem('project-id'),
    );
    const reqConfig = config;
    reqConfig.headers = {
      'gosky-api-token': token,
      ...(projectID && { 'project-id': projectID }),
    } as AxiosRequestHeaders;

    return reqConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 登入前就可訪問的頁面：這些頁面上的 401 是常態（尚未登入），不能導轉，否則登入頁會無限迴圈。
// 用路徑常數組清單，route 改名時才不會靜默失效
const PUBLIC_PATH_PREFIXES = [
  LOGIN,
  SIGNUP,
  RESET,
  VERIFY,
  FACEBOOK,
  GOOGLE,
  GOOGLE_AUTH,
  PRIVACY_POLICY,
  PROJECT_INVITE,
].map((segment) => `/${segment}`);

const isPublicPath = (pathname: string) =>
  PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 伺服器統一處理 403 跳轉至沒有專案權限
    if (error.response?.status === 403) {
      window.location.href = PAGE_PERMISSION_DENIED;
    }
    // WHY: token 過期時原本各 hook 把 401 吞成空資料，頁面默默退化（升級頁顯示 NT$0 還能結帳，
    // 2026-09 客戶因此連買四次）。統一導回登入，並帶 returnUrl 讓登入後回到原頁（含 query，
    // 例如 tap-pay-callback 的付款結果參數不能丟）。
    if (
      error.response?.status === 401 &&
      !isPublicPath(window.location.pathname)
    ) {
      // 只處理自家平台 API；mead 直連或 Meta 等第三方的 401 不能清平台 token。
      // 不能只看相對路徑：editor API 常數是 'api/v1/...'（無前導斜線），
      // 而 usePostRedirect 會把全域 baseURL 改成 mead——一律解析實際 origin 判斷
      const isPlatformApi = (() => {
        try {
          const resolved = new URL(
            error.config?.url ?? '',
            error.config?.baseURL || window.location.origin,
          );
          return resolved.origin === window.location.origin;
        } catch {
          return false;
        }
      })();
      // 登入前發出的舊請求可能在登入成功後才回 401（in-flight race）；
      // 帶的 token 與現存 token 不同代表已重新登入，不能把新 token 清掉
      const requestToken = error.config?.headers?.['gosky-api-token'];
      const currentToken = localStorage.getItem('token');
      const isStaleRequest = !!currentToken && requestToken !== currentToken;
      if (isPlatformApi && !isStaleRequest) {
        localStorage.removeItem('token');
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `${PAGE_LOGIN}?returnUrl=${returnUrl}`;
      }
    }
    return Promise.reject(error);
  },
);
